import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { clone, createCloseIcon, getElementById } from "../util/Utils";
import type { Predicate } from "../types/Predicate";
import { clearStatus, extendLabel, getStatus, updateLabel, updateStatus } from "../tracking/FeatureTracking";
import type { FeatureStatus } from "../tracking/Tracking";
import type { TrackingLabels } from "../tracking/TrackingLabels";

const FACET_VALUE_LABEL_CLASS = `hc_label`;
const FACET_VALUE_LABEL_SELECTOR = `.${FACET_VALUE_LABEL_CLASS}`;
const FACET_VALUE_CHECKBOX_CLASS = `hc_label__handle`;
const FACET_VALUE_CHECKBOX_SELECTOR = `.${FACET_VALUE_CHECKBOX_CLASS}`;
const FACET_VALUE_CLASS = `hc_facetValueInput`;
const FACET_VALUE_TITLE_CLASS = `hc_label__title`;
const FACET_VALUE_TITLE_SELECTOR = `.${FACET_VALUE_TITLE_CLASS}`;
const CLONED_NBA_ID_SUFFIX = `--nba--cloned`;

export class SvelteFacetValue {
  static readonly factory = HeurekaElementFactory.byElement("input", FACET_VALUE_CLASS, SvelteFacetValue);
  readonly label: HTMLLabelElement | null;
  readonly span: HTMLSpanElement | null;

  /*               */
  constructor(
    readonly facetValue: HTMLInputElement,
    readonly liElem: HTMLLIElement | null = facetValue.closest("li"),
  ) {
    this.label = facetValue.closest("label");
    this.span = liElem?.querySelector(FACET_VALUE_TITLE_SELECTOR);
  }

  /*                  */
  /*               */
  /*                       */

  get id(): string | undefined {
    return this.facetValue.id;
  }

  get name(): string | undefined {
    return this.facetValue.name;
  }

  get checked(): boolean {
    return this.facetValue.checked;
  }

  get nba(): boolean {
    return this.facetValue.dataset.nba === "true";
  }

  get ref(): string | undefined {
    return this.facetValue.dataset.tsFeatureRef;
  }

  get referencedElement(): HTMLElement | undefined {
    return getElementById(this.ref);
  }

  get title(): string | undefined {
    return this.span?.textContent || undefined;
  }

  set title(title: string | null | undefined) {
    if (this.span && title) {
      this.span.innerHTML = title;
    }
  }

  clone(): SvelteFacetValue {
    return SvelteFacetValue.factory.pick(undefined, clone(this.liElem));
  }

  cloneWithData(id: string, title: string): SvelteFacetValue {
    const svelteFacetValue = this.clone();
    svelteFacetValue.facetValue.id = `${id}${CLONED_NBA_ID_SUFFIX}`;
    svelteFacetValue.facetValue.dataset.tsFeatureRef = id;
    svelteFacetValue.title = title;
    return svelteFacetValue;
  }

  updateStatus(statusToUpdate: FeatureStatus, updatedStatus: FeatureStatus) {
    updateStatus(this.facetValue, updatedStatus, undefined, (oldStatus, newStatus) => {
      return oldStatus === statusToUpdate ? newStatus : oldStatus;
    });
  }

  /*                                                       */
  updateTrackingLabelsOnReferenced(status: FeatureStatus, filterMethod: TrackingLabels["san_FilterMethod"]) {
    if (this.referencedElement) {
      updateStatus(this.referencedElement, status, 2);
      updateLabel(this.facetValue, "san_FilterMethod", filterMethod);
      updateLabel(this.referencedElement, "san_FilterMethod", filterMethod);
    }
    return this;
  }

  /*                                                             */
  transformToFacetValueTag() {
    const closeIcon = createCloseIcon();

    this.label?.removeAttribute("class");
    this.label?.removeAttribute("style");
    this.facetValue.removeAttribute("class");
    this.span?.removeAttribute("class");

    this.facetValue?.classList.add("js_hc_tValue", "find_facetValueTag__deselect");
    this.label?.classList.add("pl_chip100", "pl_chip100--grey", "pl_chip100--fixes");

    const labelSpan = this.label?.querySelector<HTMLSpanElement>(FACET_VALUE_LABEL_SELECTOR);
    if (labelSpan) {
      labelSpan.removeAttribute("class");
      delete labelSpan.dataset.count;
    }
    this.label?.querySelector(FACET_VALUE_CHECKBOX_SELECTOR)?.remove();
    this.label?.appendChild(closeIcon);
    return this;
  }

  clearStatus() {
    if (this.facetValue) {
      clearStatus(this.facetValue);
    }
    return this;
  }

  withRef(ref: string | undefined) {
    if (this.facetValue && ref) {
      this.facetValue.dataset.tsFeatureRef = ref;
    }
    return this;
  }

  toggleChecked(state: boolean) {
    if (this.facetValue) {
      this.facetValue.checked = state;
      this.facetValue.defaultChecked = state;
      if (state) {
        updateStatus(this.facetValue, "selected", 2);
      } else {
        updateStatus(this.facetValue);
      }
    }
  }

  get spanInnerHTML(): string | null {
    return this.span?.innerHTML;
  }
}

export const isSelected: Predicate<SvelteFacetValue> = (value: SvelteFacetValue) => value.checked;

export const isNotSelected: Predicate<SvelteFacetValue> = (value: SvelteFacetValue) => !value.checked;

export const isNba: Predicate<SvelteFacetValue> = (value: SvelteFacetValue) => value.nba;

export const updateFacetValueTagPosition = (value: SvelteFacetValue, index: number) =>
  value.referencedElement && updateLabel(value.referencedElement, "san_FacetValueTagPosition", (index + 1).toString());

export const updateNBAPosition = (value: SvelteFacetValue, index: number) =>
  value.referencedElement && updateLabel(value.referencedElement, "san_NBAPosition", (index + 1).toString());

export const updateSanTopFacetValue = (value: SvelteFacetValue) =>
  value.referencedElement && extendLabel(value.referencedElement, "san_TopFacetValue", "nba");

export const setFeatureStatus = (value: SvelteFacetValue, status: FeatureStatus) => {
  const referencedElement = value.referencedElement;
  if (!referencedElement) return;
  const currentStatus = getStatus(referencedElement);
  if (!currentStatus || currentStatus === "loaded" || currentStatus == "visible") {
    updateStatus(referencedElement, status);
  }
};
